import { uniq, lowerCase, toLower, isArray } from 'lodash-es';
import is from 'helpers/is';

const CATEGORY_TO_EXCLUDE = 'All';
const FEATURED_CATEGORY = 'Featured';
const SLUG_REGEX = /([^\.]*)(-single-page)?/;
const DOMAIN = is().prod ? 'com' : 'wtf';
const VERSIONS_ORDER = ['onePage', 'multiPages', 'ecommerce'];
const CATEGORIES_ORDER = [
  'featured',
  'business',
  'services',
  'personal',
  'health',
  'beauty',
  'restaurant',
  'portfolio',
];

const prepareTemplates = (templates) =>
  templates
    .map((item) => ({
      ...item,
      categories: item.categories
        .filter((category) => category !== CATEGORY_TO_EXCLUDE)
        .map((category) => toLower(category)),
    }))
    .filter(
      (item) =>
        item.categories.length &&
        item.hidden !== false &&
        item.deleted !== false &&
        !/decomission/.test(item.name)
    )
    .map((item) => {
      if (item.position !== 0) {
        item.categories.unshift(toLower(FEATURED_CATEGORY));
      }

      return item;
    });

const getCategories = (templates) =>
  uniq(templates.reduce((acc, item) => [...acc, ...item.categories], []))
    .map((item) => lowerCase(item))
    .filter((item) => item !== toLower(CATEGORY_TO_EXCLUDE));

const mapVersions = (versions) =>
  versions
    .map((version) => ({
      type: version.type,
      link: `${version.link}.siteplus.${DOMAIN}`,
      id: version._id,
    }))
    .sort(
      (a, b) => VERSIONS_ORDER.indexOf(a.type) - VERSIONS_ORDER.indexOf(b.type)
    );

const mapTemplates = (templates) =>
  templates.reduce(
    (
      acc,
      {
        title,
        categories,
        templateIndex,
        versions,
        versionName,
        position,
        locale,
      }
    ) => [
      ...acc,
      {
        thumb: versions[0].src.name,
        title,
        categories,
        index: templateIndex,
        position,
        slug: SLUG_REGEX.exec(versions[0].link)[1],
        versions: mapVersions(versions),
        defaultVersion: versionName,
        locale: locale[0] !== 'en' ? locale : false,
      },
    ],
    []
  );

const mapCategoriesToTabs = (categories) =>
  categories
    .sort((a, b) => CATEGORIES_ORDER.indexOf(a) - CATEGORIES_ORDER.indexOf(b))
    .map((tab, key) => ({
      link: key === 0 ? '' : `/${tab}`,
      text: tab,
    }));

const mapCategoriesToRoutes = (categories) =>
  categories.map((tab, key) => ({
    path: `/templates${tab !== categories[0] ? `/${lowerCase(tab)}` : ''}`,
    tab: lowerCase(tab),
    key,
  }));

const groupTemplatesByCategory = (templates, categories) =>
  categories.reduce(
    (acc, category) => ({
      ...acc,
      [category]: templates.filter(
        ({ categories, locale }) =>
          categories.includes(category) && locale === false
      ),
    }),
    {}
  );

const groupTemplates = (templates, categories) => {
  const categorized = groupTemplatesByCategory(templates, categories);
  categorized.localized = templates.filter(({ locale }) => isArray(locale));

  return categorized;
};

export default (allTemplates) => {
  const templates = prepareTemplates(allTemplates);
  const categories = getCategories(templates);
  return {
    categories,
    tabs: mapCategoriesToTabs(categories),
    routes: mapCategoriesToRoutes(categories),
    templates: groupTemplates(mapTemplates(templates), categories),
  };
};
